<template>
  <q-page
    class="fit"
    style="overflow-y: hidden"
  >
    <!-- No Accounts -->
    <div
      v-if="exchangeAccountsArray.length == 0 && !exchange_accounts_search"
      class="flex items-start justify-center"
      style="height: 100%; padding-top: 20vh"
    >
      <div class="text-center">
        <img
          class="q-mx-auto"
          src="/illustrations/tax_report_ob4.svg"
          width="320"
        >

        <div class="text-center q-my-lg">
          <h3 class="text-regular text-h3 q-mb-md">
            {{ i18n.no_exchange_account_title }}
          </h3>
          <p class="text-body1 q-mb-xl">
            {{ i18n.no_exchange_account_subtitle }}
          </p>
        </div>
        <div class="text-center">
          <Link
            color="primary"
            :href="$page.props.links.add_account.link"
          >
            <q-btn
              unelevated
              color="primary"
            >
              {{ $page.props.links.add_account.title }}
            </q-btn>
          </Link>
        </div>
      </div>
    </div>

    <!-- ExchangeAccounts -->
    <template v-else>
      <!-- Left panel, exchange account list -->
      <div
        :class="{
          'col-8': exchange_account?.id != undefined,
          'col-12': exchange_account?.id == undefined,
          'width-transition': true,
        }"
        style="min-height: inherit"
      >
        <div class="centered-container fit min-w-[238px]">
          <div class="row wrap justify-between items-center content-start q-pa-xl">
            <span class="text-h4">{{ i18n.title }}</span>

            <div class="row items-center">
              <Link
                v-if="exchangeAccountsArray.length > 0 || exchange_accounts_search"
                :href="$page.props.links.add_account.link"
              >
                <q-btn
                  unelevated
                  icon="las la-plus"
                  color="primary"
                  :label="$page.props.links.add_account.title"
                />
              </Link>

              <q-btn
                flat
                icon="las la-sync"
                :loading="sync_all_loading"
                :label="$page.props.links.sync_all.title"
                @click="sync_all()"
              />
            </div>
          </div>

          <div class="row q-mt-none justify-between q-px-xl q-pb-md full-width">
            <!-- Search -->
            <div class="col-auto">
              <q-input
                :model-value="exchange_accounts_search"
                :debounce="300"
                outlined
                color="drawer-background"
                standout
                hide-bottom-space
                dense
                :placeholder="i18n.search_placeholder"
                @update:model-value="exchange_account_search_update"
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
                <template v-slot:append>
                  <q-icon
                    v-if="exchange_accounts_search != ''"
                    name="close"
                    class="cursor-pointer"
                    @click="exchange_account_search_update('')"
                  />
                </template>
              </q-input>
            </div>
            <div
              v-if="false"
              class="col-shrink text-right"
            >
              <q-btn
                round
                icon="grid_view"
                :color="view === 'grid' ? 'accent' : 'primary'"
                flat
                @click="setView('grid')"
              />
              <q-btn
                round
                icon="format_list_bulleted"
                :color="view === 'list' ? 'accent' : 'primary'"
                flat
                @click="setView('list')"
              />
            </div>
          </div>

          <!-- grid -->
          <div
            v-show="view == 'grid'"
            style="max-height: calc(100vh - 400px)"
            class="row q-mt-none justify-start q-px-xl q-col-gutter-lg scroll q-pb-xl"
          >
            <div
              v-for="group in exchangeAccountGroups"
              :key="group.exchange_id"
              class="col-xs-12 col-sm-6 col-md-4 col-lg-4"
            >
              <!--
              <ExchangeAccountItemCardGrid
                :exchangeAccount="exchange_account"
                :accountData="historyStore.histories[exchange_account.id]"
                @click="selectedExchangeAccountID = exchange_account.id"
                @settings="showExchangeAccountSettings(exchange_account.id)"
                :highlight="exchange_account.id == selectedExchangeAccountID"
              />
              -->
            </div>
          </div>

          <!-- ExchangeAccounts -->
          <div
            v-show="view == 'list'"
            class="row q-mt-none justify-start q-px-xl q-col-gutter-lg scroll q-pb-xl"
          >
            <div
              v-for="group in exchangeAccountGroups"
              :key="group.exchange_id"
              class="col-12"
            >
              <!-- Single account: keep current behavior -->
              <template v-if="group.accounts.length === 1">
                <q-card
                  v-ripple:blue
                  class="cursor-pointer q-hoverable full-width q-pa-sm card-item"
                  :class="{ highlighted: group.accounts[0].id == exchange_account?.id }"
                  @click="exchange_account_select(group.accounts[0].id)"
                >
                  <span class="q-focus-helper" />

                  <q-item>
                    <!-- Exchange icon -->
                    <q-item-section
                      avatar=""
                      class="gt-xs"
                    >
                      <q-avatar size="28px">
                        <img
                          :alt="group.accounts[0].exchange_name"
                          :src="group.accounts[0].logo_url"
                        >
                      </q-avatar>
                    </q-item-section>

                    <!-- Exchange Account name and platforme + status -->
                    <q-item-section class="q-pl-sm">
                      <q-item-label
                        caption
                        lines="1"
                        class="text-caption text-capitalize"
                      >
                        {{ group.accounts[0].exchange_name }}
                      </q-item-label>

                      <q-item-label class="ellipsis text-bold items-center">
                        <!-- Exchange account status -->
                        <q-spinner
                          v-if="group.accounts[0].loading"
                          class="q-mr-xs inline"
                          :color="group.accounts[0].status.color"
                          size="14px"
                        />
                        <q-icon
                          v-else
                          size="14px"
                          :name="group.accounts[0].status.icon"
                          :color="group.accounts[0].status.color"
                          class="q-mr-xs"
                        />
                        <q-tooltip
                          anchor="center left"
                          self="center right"
                          class="q-card shadow text-caption text-default q-pa-md q-menu"
                          :delay="500"
                        >
                          <p class="q-mb-none">
                            {{ group.accounts[0].status.message_long }}
                          </p>
                        </q-tooltip>

                        <!-- Balance audit status -->
                        <span
                          v-if="group.accounts[0].balance_audit_matched === false"
                          class="q-mr-xs"
                        >
                          <q-icon
                            name="info"
                            color="warning"
                            size="16px"
                            class="q-ml-xs"
                          />
                          <q-tooltip
                            anchor="center left"
                            self="center right"
                            class="q-card shadow text-caption text-default q-pa-md q-menu"
                            :delay="500"
                          >
                            <p class="q-mb-none">
                              {{ i18n.balance_audit_missmatch }}
                            </p>
                          </q-tooltip>
                        </span>

                        {{ group.accounts[0].name }}
                      </q-item-label>
                    </q-item-section>

                    <!-- Spacer -->
                    <q-item-section class="gt-sm" />

                    <!-- Balance -->
                    <q-item-section class="gt-sm">
                      <q-item-label class="text-caption">
                        {{ i18n.balance }}:
                      </q-item-label>
                      <q-item-label
                        lines="1"
                        class="text-bold"
                      >
                        <template v-if="group.accounts[0].loading">
                          <q-spinner-dots />
                        </template>
                        <template v-else>
                          {{ group.accounts[0].balance }}
                        </template>
                      </q-item-label>
                    </q-item-section>

                    <!-- Transactions -->
                    <q-item-section class="gt-sm">
                      <q-item-label class="text-caption">
                        {{ i18n.transactions }}:
                      </q-item-label>
                      <q-item-label
                        lines="1"
                        class="text-bold"
                      >
                        {{ group.accounts[0].total_transactions_count }}
                      </q-item-label>
                    </q-item-section>

                    <!-- Assets -->
                    <q-item-section class="gt-md">
                      <q-item-label class="text-caption">
                        {{ i18n.assets }}:
                      </q-item-label>
                      <q-item-label
                        lines="1"
                        class="text-bold"
                      >
                        <div
                          v-for="(icon, index) in group.accounts[0].assets?.icons"
                          :key="index"
                          class="inline-block"
                        >
                          <img
                            width="18px"
                            height="18px"
                            class="inline q-mr-xs"
                            :alt="icon.symbol"
                            :src="icon.url"
                          >
                        </div>
                        <span v-if="group.accounts[0].assets.assets_count"> {{ group.accounts[0].assets.assets_count }} </span>
                        <span v-if="group.accounts[0].assets?.icons?.length === 0"> {{ i18n.none }} </span>
                      </q-item-label>
                    </q-item-section>

                    <!-- Chevron right -->
                    <q-item-section side>
                      <q-icon name="chevron_right" />
                    </q-item-section>
                  </q-item>
                </q-card>
              </template>

              <!-- Group: collapsed/expanded accounts list -->
              <template v-else>
                <q-card
                  v-ripple:blue
                  class="cursor-pointer q-hoverable full-width q-pa-sm card-item"
                  :class="{ highlighted: group.accounts.some((a) => a.id == exchange_account?.id) }"
                  @click.stop="exchange_group_toggle(group.exchange_id)"
                >
                  <span class="q-focus-helper" />

                  <q-item>
                    <!-- Exchange icon -->
                    <q-item-section
                      avatar=""
                      class="gt-xs"
                    >
                      <q-avatar size="28px">
                        <img
                          :alt="group.exchange_name"
                          :src="group.logo_url"
                        >
                      </q-avatar>
                    </q-item-section>

                    <!-- Exchange name + status -->
                    <q-item-section class="q-pl-sm">
                      <q-item-label
                        caption
                        lines="1"
                        class="text-caption text-capitalize"
                      >
                        {{ group.exchange_name }}
                      </q-item-label>

                      <q-item-label class="ellipsis text-bold items-center">
                        <q-spinner
                          v-if="group.loading"
                          class="q-mr-xs inline"
                          :color="group.status.color"
                          size="14px"
                        />
                        <q-icon
                          v-else
                          size="14px"
                          :name="group.status.icon"
                          :color="group.status.color"
                          class="q-mr-xs"
                        />
                        <q-tooltip
                          anchor="center left"
                          self="center right"
                          class="q-card shadow text-caption text-default q-pa-md q-menu"
                          :delay="500"
                        >
                          <p class="q-mb-none">
                            {{ group.status.message_long }}
                          </p>
                        </q-tooltip>

                        <span
                          v-if="group.balance_audit_matched === false"
                          class="q-mr-xs"
                        >
                          <q-icon
                            name="info"
                            color="warning"
                            size="16px"
                            class="q-ml-xs"
                          />
                          <q-tooltip
                            anchor="center left"
                            self="center right"
                            class="q-card shadow text-caption text-default q-pa-md q-menu"
                            :delay="500"
                          >
                            <p class="q-mb-none">
                              {{ i18n.balance_audit_missmatch }}
                            </p>
                          </q-tooltip>
                        </span>

                        {{ accountsLabel(group.accounts.length) }}
                      </q-item-label>
                    </q-item-section>

                    <!-- Spacer -->
                    <q-item-section class="gt-sm" />

                    <!-- Balance -->
                    <q-item-section class="gt-sm">
                      <q-item-label class="text-caption">
                        {{ i18n.balance }}:
                      </q-item-label>
                      <q-item-label
                        lines="1"
                        class="text-bold"
                      >
                        <template v-if="group.loading">
                          <q-spinner-dots />
                        </template>
                        <template v-else>
                          {{ formatFiatFromCents(group.balance_cents) }}
                        </template>
                      </q-item-label>
                    </q-item-section>

                    <!-- Transactions -->
                    <q-item-section class="gt-sm">
                      <q-item-label class="text-caption">
                        {{ i18n.transactions }}:
                      </q-item-label>
                      <q-item-label
                        lines="1"
                        class="text-bold"
                      >
                        {{ group.total_transactions_count }}
                      </q-item-label>
                    </q-item-section>

                    <!-- Assets -->
                    <q-item-section class="gt-md">
                      <q-item-label class="text-caption">
                        {{ i18n.assets }}:
                      </q-item-label>
                      <q-item-label
                        lines="1"
                        class="text-bold"
                      >
                        <div
                          v-for="(icon, index) in group.assets?.icons"
                          :key="index"
                          class="inline-block"
                        >
                          <img
                            width="18px"
                            height="18px"
                            class="inline q-mr-xs"
                            :alt="icon.symbol"
                            :src="icon.url"
                          >
                        </div>
                        <span v-if="group.assets.assets_count"> {{ group.assets.assets_count }} </span>
                        <span v-if="group.assets.icons.length === 0"> {{ i18n.none }} </span>
                      </q-item-label>
                    </q-item-section>

                    <!-- Chevron -->
                    <q-item-section side>
                      <q-icon :name="is_exchange_group_expanded(group.exchange_id) ? 'expand_less' : 'expand_more'" />
                    </q-item-section>
                  </q-item>
                </q-card>

                <div class="q-pl-xl">
                  <div
                    v-show="is_exchange_group_expanded(group.exchange_id)"
                    class="q-mt-sm"
                  >
                    <div
                      v-for="ea in group.accounts"
                      :key="ea.id"
                      class="q-mb-md"
                    >
                      <q-card
                        v-ripple:blue
                        class="cursor-pointer q-hoverable full-width q-pa-sm card-item"
                        :class="{ highlighted: ea.id == exchange_account?.id }"
                        @click="exchange_account_select(ea.id)"
                      >
                        <span class="q-focus-helper" />

                        <q-item>
                          <!-- Exchange icon -->
                          <q-item-section
                            avatar=""
                            class="gt-xs"
                          >
                            <q-avatar size="28px">
                              <img
                                :alt="ea.exchange_name"
                                :src="ea.logo_url"
                              >
                            </q-avatar>
                          </q-item-section>

                          <!-- Exchange Account name and platforme + status -->
                          <q-item-section class="q-pl-sm">
                            <q-item-label
                              caption
                              lines="1"
                              class="text-caption text-capitalize"
                            >
                              {{ ea.exchange_name }}
                            </q-item-label>

                            <q-item-label class="ellipsis text-bold items-center">
                              <!-- Exchange account status -->
                              <q-spinner
                                v-if="ea.loading"
                                class="q-mr-xs inline"
                                :color="ea.status.color"
                                size="14px"
                              />
                              <q-icon
                                v-else
                                size="14px"
                                :name="ea.status.icon"
                                :color="ea.status.color"
                                class="q-mr-xs"
                              />
                              <q-tooltip
                                anchor="center left"
                                self="center right"
                                class="q-card shadow text-caption text-default q-pa-md q-menu"
                                :delay="500"
                              >
                                <p class="q-mb-none">
                                  {{ ea.status.message_long }}
                                </p>
                              </q-tooltip>

                              <!-- Balance audit status -->
                              <span
                                v-if="ea.balance_audit_matched === false"
                                class="q-mr-xs"
                              >
                                <q-icon
                                  name="info"
                                  color="warning"
                                  size="16px"
                                  class="q-ml-xs"
                                />
                                <q-tooltip
                                  anchor="center left"
                                  self="center right"
                                  class="q-card shadow text-caption text-default q-pa-md q-menu"
                                  :delay="500"
                                >
                                  <p class="q-mb-none">
                                    {{ i18n.balance_audit_missmatch }}
                                  </p>
                                </q-tooltip>
                              </span>

                              {{ ea.name }}
                            </q-item-label>
                          </q-item-section>

                          <!-- Spacer -->
                          <q-item-section class="gt-sm" />

                          <!-- Balance -->
                          <q-item-section class="gt-sm">
                            <q-item-label class="text-caption">
                              {{ i18n.balance }}:
                            </q-item-label>
                            <q-item-label
                              lines="1"
                              class="text-bold"
                            >
                              <template v-if="ea.loading">
                                <q-spinner-dots />
                              </template>
                              <template v-else>
                                {{ ea.balance }}
                              </template>
                            </q-item-label>
                          </q-item-section>

                          <!-- Transactions -->
                          <q-item-section class="gt-sm">
                            <q-item-label class="text-caption">
                              {{ i18n.transactions }}:
                            </q-item-label>
                            <q-item-label
                              lines="1"
                              class="text-bold"
                            >
                              {{ ea.total_transactions_count }}
                            </q-item-label>
                          </q-item-section>

                          <!-- Assets -->
                          <q-item-section class="gt-md">
                            <q-item-label class="text-caption">
                              {{ i18n.assets }}:
                            </q-item-label>
                            <q-item-label
                              lines="1"
                              class="text-bold"
                            >
                              <div
                                v-for="(icon, index) in ea.assets?.icons"
                                :key="index"
                                class="inline-block"
                              >
                                <img
                                  width="18px"
                                  height="18px"
                                  class="inline q-mr-xs"
                                  :alt="icon.symbol"
                                  :src="icon.url"
                                >
                              </div>
                              <span v-if="ea.assets.assets_count"> {{ ea.assets.assets_count }} </span>
                              <span v-if="ea.assets?.icons?.length === 0"> {{ i18n.none }} </span>
                            </q-item-label>
                          </q-item-section>

                          <!-- Chevron right -->
                          <q-item-section side>
                            <q-icon name="chevron_right" />
                          </q-item-section>
                        </q-item>
                      </q-card>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div
            v-if="exchangeAccountsArray.length == 0 && exchange_accounts_search"
            class="text-center q-pa-md"
          >
            <q-icon
              name="search_off"
              size="md"
              color="grey-7"
            />
            <p class="text-caption q-mt-sm q-mb-none text-grey-7">
              {{ i18n.exchange_accounts_search_no_results }}
            </p>
          </div>

          <div class="col-12 q-mt-md q-px-xl">
            <!-- Banner info about someExchangeAccountsAreCrawling -->
            <q-banner
              class="bg-pastel-blue q-pa-md"
              dense
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-info-circle"
                  class=""
                  size="lg"
                />
              </template>
              <div class="text-caption text-dark">
                {{ i18n.banner_crawling_delay }}
              </div>
            </q-banner>
          </div>

          <div
            v-if="exchanges_in_maintenance.length > 0"
            class="col-12 q-mt-md q-px-xl"
          >
            <q-banner
              class="bg-pastel-orange q-pa-md"
              dense
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-exclamation-triangle"
                  size="lg"
                />
              </template>
              <div class="text-caption text-dark">
                {{ i18n.banner_maintenance }}
              </div>
            </q-banner>
          </div>
        </div>
      </div>
      <!-- /ExchangeAccounts -->
    </template>
  </q-page>

  <!-- Right panel, exchange account details -->
  <q-drawer
    v-if="exchange_account?.id"
    :model-value="exchange_account.id != void 0"
    container
    behavior="desktop"
    side="right"
    :width="480"
    class="exchange-account-sidebar px-8"
  >
    <!-- Header row  -->
    <q-item class="px-0 my-4">
      <q-item-section avatar>
        <q-avatar size="42px">
          <transition
            :key="exchange_account.id"
            appear
            enter-active-class="animated bounceIn"
            leave-active-class="animated bounceOut"
            :duration="500"
          >
            <img
              :alt="exchange_account.exchange_name"
              :src="exchange_account.logo_url"
            >
          </transition>
        </q-avatar>
      </q-item-section>
      <q-item-section>
        <q-item-label class="text-bold">
          {{ exchange_account.exchange_name }}
        </q-item-label>
        <q-item-label caption>
          {{ exchange_account.name }}
        </q-item-label>
      </q-item-section>

      <q-item-section
        side
        class="flex"
        style="display: inline"
      >
        <q-btn
          unelevated
          flat
          round
          icon="more_vert"
        >
          <q-menu>
            <q-list style="min-width: 220px">
              <q-item
                v-close-popup
                clickable
                @click="exchange_account_rename_open"
              >
                <q-item-section avatar>
                  <q-icon
                    name="las la-pen"
                    color="primary"
                  />
                </q-item-section>
                <q-item-section>Renommer le compte</q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                @click="initial_balance_dialog_show = true"
              >
                <q-item-section avatar>
                  <q-icon
                    name="las la-balance-scale"
                    color="primary"
                  />
                </q-item-section>
                <q-item-section>Créer un solde initial</q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                @click="exchange_account_delete_confirm"
              >
                <q-item-section avatar>
                  <q-icon
                    name="las la-trash"
                    color="negative"
                  />
                </q-item-section>
                <q-item-section>Supprimer le compte</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
        <q-btn
          unelevated
          flat
          round
          icon="close"
          @click="exchange_account_close"
        />
      </q-item-section>
    </q-item>

    <q-separator class="q-mb-lg" />

    <!-- Error banner for importer errors -->
    <template v-if="exchange_account.importer_errors_count > 0">
      <div class="col-12 q-mt-md q-pa-md">
        <q-banner
          class="bg-pastel-red q-px-md"
          dense
        >
          <template v-slot:avatar>
            <q-icon
              name="las la-exclamation-triangle"
              color="black"
              size="sm"
            />
          </template>
          <div class="text-dark">
            {{ i18n.import_error_occured }}
          </div>
        </q-banner>
      </div>

      <div class="col-12 q-mt-none text-center">
        <Link :href="exchange_account.links.importer_errors.link">
          <q-btn
            unelevated
            color="negative"
            class="btn-underline"
            flat
            :label="exchange_account.links.importer_errors.title"
            size="md"
            no-caps
          />
        </Link>
      </div>
    </template>
    <!-- Balance audit missmatch banner -->
    <template v-if="exchange_account.balance_audit_matched === false">
      <div class="col-12 q-mt-md q-pa-md">
        <q-banner
          inline-actions
          class="bg-pastel-orange q-px-md text-left"
          dense
        >
          <div class="text-dark">
            <q-icon
              name="info"
              color="black"
              size="sm"
            />
            {{ i18n.balance_discrepency_detected }}
          </div>
          <template v-slot:action>
            <Link :href="exchange_account.links.balance_audit.link">
              <q-btn
                unelevated
                color="text-dark"
                class="btn-underline"
                flat
                :label="exchange_account.links.balance_audit.title"
                size="md"
                no-caps
              />
            </Link>
          </template>
        </q-banner>
      </div>
    </template>

    <!-- Balance -->
    <q-item
      class="mt-4 bg-surface py-4"
      clickable
      @click="exchange_account_open_dashboard"
    >
      <q-item-section avatar>
        <q-icon
          name="las la-wallet"
          size="28px"
          color="primary"
        />
      </q-item-section>

      <q-item-section>
        <q-item-label class="text-h6 text-bold">
          {{ exchange_account.balance }}


          <q-badge
            v-if="exchange_account_missing_change_rate"
            style="position: absolute; top: 18px"
            color="transparent"
          >
            <q-icon
              name="sym_o_warning"
              color="warning"
              size="16px"
            />
            <q-tooltip
              anchor="bottom middle"
              class="bg-warning text-white"
              self="top middle"
            >
              <span>{{ i18n.exchange_account_summary_warning }}</span>
            </q-tooltip>
          </q-badge>
        </q-item-label>
        <q-item-label class="text-caption">
          {{ exchange_account.balance_date || i18n.balance }}
        </q-item-label>
      </q-item-section>

      <q-item-section side>
        <q-icon name="chevron_right" />
      </q-item-section>
    </q-item>

    <!-- Transactions -->
    <q-item
      class="mt-4 bg-surface py-4"
      clickable
      @click="exchange_account_open_transactions"
    >
      <q-item-section avatar>
        <q-icon
          name="las la-exchange-alt"
          size="28px"
          color="primary"
        />
      </q-item-section>

      <q-item-section>
        <q-item-label class="text-h6 text-bold">
          {{ exchange_account.total_transactions_count }}
        </q-item-label>
        <q-item-label class="text-caption">
          {{ i18n.exchange_account_details_transactions_label }}
        </q-item-label>
      </q-item-section>

      <q-item-section side>
        <q-icon name="chevron_right" />
      </q-item-section>
    </q-item>

    <q-separator class="q-my-lg" />

   
    <!-- Assets Table -->
    <p class="sidebar-section-title">
      {{ i18n.exchange_account_assets_table_title }}
    </p>

    <q-table
      v-if="exchange_account_details_graph_selected == 'table'"
      id="assets-table"
      v-model:pagination="assets_table_pagination"
      :rows="exchange_account.assets_table"
      :columns="assets_table_columns"
      row-key="id"
      class="full-width q-pt-xs"
      flat
      dense
      :no-data-label="$q.lang.table.noResults"
      separator="none"
      hide-pagination
      style="max-height: 340px"
      :rows-per-page-options="[0]"
    >
      <template v-slot:body-cell-asset_symbol="props">
        <q-td
          :props="props"
          class=" "
        >
          <div class="col-shrink">
            <!-- Asset Icon -->
            <img
              width="18px"
              height="18px"
              class="inline q-mr-xs"
              :alt="props.value"
              :src="props.row.asset_icon"
            >

            {{ props.row.asset_symbol_truncated }}
            <q-tooltip
              v-if="props.value != props.row.asset_symbol_truncated"
              :delay="500"
              anchor="bottom middle"
              self="top middle"
            >
              {{ props.value }}
            </q-tooltip>
          </div>
        </q-td>
      </template>

      <template v-slot:body-cell-fiat_value_formatted="props">
        <q-td
          :props="props"
          class=" "
        >
          <span class="text-bold">
            {{ props.value }}
            <q-tooltip
              :delay="500"
              anchor="center start"
              self="center right"
              :offset="[50, 0]"
            >{{ props.row.change_rate_text }}</q-tooltip>
          </span>
          <q-badge
            v-if="props.row.change_rate == null || props.row.change_rate == '0.0'"
            color="transparent"
          >
            <q-icon
              name="fas la-exclamation-triangle"
              color="warning"
              size="14px"
            />
            <q-tooltip
              anchor="center left"
              self="center right"
              class="bg-warning text-white"
            >
              <span>{{ i18n.table_column_value_error }}</span>
            </q-tooltip>
          </q-badge>
        </q-td>
      </template>
    </q-table>

    <q-separator class="q-my-lg" />

    <!-- Account Details -->
    <p class="sidebar-section-title">
      {{ i18n.exchange_account_details_title }}
    </p>

    <q-list class="">
      <!-- Platform -->
      <q-item class="detailed-list-item items-center px-0">
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.exchange_account_details_platform_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ exchange_account.exchange_name }}
          </q-item-label>
        </q-item-section>
      </q-item>

      <!-- API Key -->
      <q-item
        v-if="exchange_account.api_key"
        class="detailed-list-item items-center px-0"
      >
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.exchange_account_details_api_key_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ string_ellipsis_middle(exchange_account.api_key) }}
          </q-item-label>
        </q-item-section>
        <q-item-section
          side
          class="flex"
          style="display: inline"
        >
          <ClipboardCopy :content="exchange_account.api_key" />
        </q-item-section>
      </q-item>

      <!-- Public Address -->
      <q-item
        v-if="exchange_account.public_address"
        class="detailed-list-item items-center px-0"
      >
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.exchange_account_details_public_address_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ string_ellipsis_middle(exchange_account.public_address, 6, 6) }}
          </q-item-label>
        </q-item-section>
        <q-item-section
          side
          class="flex"
          style="display: inline"
        >
          <ClipboardCopy :content="exchange_account.public_address" />

          <q-btn
            v-if="exchange_account.explorer_url"
            unelevated
            flat
            round
            color="primary"
            size="sm"
            target="_blank"
            icon="las la-external-link-alt"
            :href="exchange_account.explorer_url"
          >
            <q-tooltip
              :delay="500"
              anchor="bottom middle"
              self="top middle"
            >
              {{ i18n.exchange_account_details_explorer_url_label }}
            </q-tooltip>
          </q-btn>
        </q-item-section>
      </q-item>

      <!-- Creation Date -->
      <q-item class="detailed-list-item items-center px-0">
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.exchange_account_details_created_at_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ exchange_account.created_at }}
          </q-item-label>
        </q-item-section>
      </q-item>

      <!-- Refresh Date -->
      <q-item
        v-if="exchange_account.crawled_at"
        class="detailed-list-item items-center px-0"
      >
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.exchange_account_details_crawled_at_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ exchange_account.crawled_at }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-list>

    <q-separator class="q-mt-lg q-mb-sm" />


    <!-- Uploaded Files -->
    <template v-if="exchange_account?.import_sources?.length > 0">
      <div class="row items-center justify-between q-mb-sm">
        <p class="sidebar-section-title q-mb-none">
          {{ i18n.exchange_account_uploads_title }}
        </p>
        <q-btn
          round
          flat
          color="primary"
          size="sm"
          icon="add"
          @click="exchange_account_uploads_dialog_open"
        >
          <q-tooltip
            :delay="500"
            anchor="bottom middle"
            self="top middle"
          >
            {{ i18n.exchange_account_uploads_files_add_more }}
          </q-tooltip>
        </q-btn>
      </div>

      <q-list class="q-pb-md">
        <q-item
          v-for="is in exchange_account.import_sources"
          :key="is.id"
          class="detailed-list-item items-center px-0"
        >
          <q-item-section avatar>
            <q-icon
              name="attach_file"
              color="text-muted"
              size="xs"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-secondary">
              {{ is.filename }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-btn
              round
              flat
              color="primary"
              size="sm"
              icon="more_vert"
            >
              <q-menu>
                <q-list style="min-width: 100px">
                  <Link
                    :href="is.link"
                  >
                    <q-item
                      v-close-popup
                      clickable
                    >
                      <q-item-section avatar>
                        <q-icon
                          name="las la-exchange-alt"
                          color="primary"
                        />
                      </q-item-section>
                      <q-item-section>{{ i18n.exchange_account_details_transactions_label }}</q-item-section>
                    </q-item>
                  </Link>
                  <q-item
                    v-close-popup
                    clickable
                    :href="is.file_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="las la-download"
                        color="primary"
                      />
                    </q-item-section>
                    <q-item-section>{{ i18n.exchange_account_uploads_files_download }}</q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    @click="exchange_account_import_source_remove_confirm(is)"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="las la-trash"
                        color="negative"
                      />
                    </q-item-section>
                    <q-item-section>{{ i18n.exchange_account_uploads_files_delete }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </q-item-section>
        </q-item>
      </q-list>
    </template>

    <div v-else>
      <p class="sidebar-section-title">
        {{ i18n.exchange_account_uploads_title }}
      </p>
      <div class="text-center">
        <div class="mb-4 shadow inline-flex items-center justify-center flex flex-col gap-2 rounded-xl p-2">
          <q-icon
            name="checklist"
            size="28px"
            color="primary"
          />
        </div>
        <p class="text-primary font-medium text-sm">
          {{ i18n.exchange_account_uploads_files_empty }}
        </p>
        <q-btn
          color="primary"
          class="q-mt-sm"
          dense
          no-caps
          unelevated
          :label="i18n.exchange_account_uploads_files_add"
          @click="exchange_account_uploads_dialog_open"
        />
      </div>
    </div>

    <q-separator class="q-my-lg" />

    <!-- Foreign Declaration -->
    <p class="sidebar-section-title">
      {{ i18n.foreign_declaration.title }}
    </p>

    <q-list class="q-pt-none">
      <q-item
        v-if="exchange_account.declaration?.legal_entity"
        class="detailed-list-item declaration-row px-0"
      >
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.foreign_declaration.legal_entity_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ exchange_account.declaration.legal_entity }}
          </q-item-label>
        </q-item-section>
      </q-item>

      <q-item
        v-if="exchange_account.declaration?.address && declaration_status.status === 'required'"
        class="detailed-list-item items-start declaration-row mb-4 px-0"
      >
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.foreign_declaration.address_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ exchange_account.declaration.address }}
          </q-item-label>
        </q-item-section>
      </q-item>

      <q-item
        v-if="exchange_account.declaration?.website"
        class="detailed-list-item items-center declaration-row px-0"
      >
        <q-item-section
          side
          class="q-pr-xs"
        >
          <q-item-label>
            {{ i18n.foreign_declaration.website_label }}
          </q-item-label>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-secondary">
            {{ truncate_url_24(exchange_account.declaration.website) }}
          </q-item-label>
        </q-item-section>
        <q-item-section
          side
          class="flex"
          style="display: inline"
        >
          <q-btn
            unelevated
            flat
            round
            color="primary"
            size="sm"
            target="_blank"
            icon="las la-external-link-alt"
            :href="exchange_account.declaration.website"
          >
            <q-tooltip
              :delay="500"
              anchor="bottom middle"
              self="top middle"
            >
              {{ i18n.foreign_declaration.website_label }}
            </q-tooltip>
          </q-btn>
        </q-item-section>
      </q-item>
    </q-list>

    <q-item
      dense
      class="full-width q-pa-none bg-surface mt-4"
    >
      <q-item-section avatar>
        <q-icon
          :name="declaration_status.message_icon"
          :color="declaration_status.color"
        />
      </q-item-section>
      <q-item-section>
        <q-item-label class="text-secondary">
          {{ declaration_status.message }}
        </q-item-label>
      </q-item-section>
    </q-item>
  </q-drawer>

  <q-dialog
    v-model="exchange_account_rename_dialog_show"
    persistent
  >
    <q-card
      class="q-pa-md"
      style="width: 420px; max-width: 90vw"
    >
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          {{ i18n.settings_account_name }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <q-input
          v-model="exchange_account_name"
          class="full-width"
          outlined
          dense
          :rules="[(val) => rule_string_length(val)]"
        />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn
          v-close-popup
          flat
          :label="$q.lang.label.cancel"
        />
        <q-btn
          :loading="exchange_account_update_loading"
          :disable="exchange_account_name === exchange_account?.name"
          :label="i18n.settings_save"
          color="primary"
          unelevated
          @click="exchange_account_update"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Balance audit dialog -->
  <q-dialog
    v-model="balance_audit_show"
    persistent
  >
    <q-card
      class="q-pa-md"
      style="max-width: inherit"
    >
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          <span class="text-capitalize">{{ exchange_account.exchange_name }}
          </span>
          - {{ i18n.balance_audit_discrepency_detected }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>

      <q-card-section>
        <p class="q-mb-xs">
          {{ i18n.balance_audit_missmatch }}
        </p>
        <p>{{ i18n.balance_audit_last_requested }} <span class="text-muted">{{ exchange_account.balance_audit.requested_at }}</span></p>
      </q-card-section>

      <q-card-section class="q-pt-sm">
        <q-table
          :rows="exchange_account.balance_audit.rows"
          :columns="exchange_account.balance_audit.headers"
          row-key="name"
          flat
          :hide-header="false"
          hide-pagination
          :rows-per-page-options="[0]"
        >
          <template v-slot:body="props">
            <q-tr :props="props">
              <q-td
                key="asset"
                :props="props"
                class="text-right"
              >
                <div class="row items-center">
                  <img
                    width="18px"
                    height="18px"
                    class="inline q-mr-sm"
                    :alt="props.row.name"
                    :src="props.row.logo"
                  >

                  {{ props.row.symbol }}
                  <q-tooltip>
                    {{ props.row.remote_id }}
                  </q-tooltip>
                </div>
              </q-td>
              <q-td
                key="internal"
                :props="props"
                class="text-right"
              >
                {{ props.row.internal }}
              </q-td>
              <q-td
                key="external"
                :props="props"
                class="text-right"
              >
                {{ props.row.external }}
              </q-td>
              <q-td
                key="diff"
                :props="props"
                class="text-right"
                :class="props.row.color"
              >
                {{ props.row.diff }}
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-close-popup
          flat
          :label="$q.lang.label.close"
          color="primary"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Import errors dialog -->
  <q-dialog
    :model-value="exchange_account?.importer_errors_show"
    persistent
  >
    <q-card
      class="q-pa-md q-pt-lg"
      style="width: 700px; max-width: 80vw"
    >
      <div class="row text-right">
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </div>
      <div class="row text-center">
        <div class="col-12 text-center">
          <q-icon
            name="las la-info-circle"
            size="56px"
            color="negative"
          />
        </div>

        <div class="col-12 q-py-md">
          <span class="text-h6">{{ i18n.error_reporting_title }}</span>
          <p class="q-pt-sm text-caption text-muted">
            {{ i18n.error_reporting_subtitle }}
          </p>
        </div>
        <q-list
          class="text-left"
          dense
        >
          <!-- Display only 5 errors -->
          <q-item
            v-for="(error, index) in exchange_account?.importer_errors.slice(0, 5)"
            :key="index"
          >
            <q-item-section avatar>
              <q-icon
                name="las la-exclamation-triangle"
                color="warning"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ error }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <!-- + xx errors  -->
        <div
          v-if="exchange_account?.importer_errors_count > 5"
          class="col-12 text-center q-py-sm"
        >
          <span class="text-caption text-grey-8">{{ i18n.error_reporting_more_errors }}</span>
        </div>

        <p class="q-px-md q-mt-xl text-bold q-mb-none">
          {{ i18n.error_reporting_csv_note }}
        </p>

        <q-banner
          class="bg-pastel-purple q-px-md q-mx-md q-my-lg text-left fit"
          dense
        >
          <template v-slot:avatar>
            <q-icon
              name="las la-info-circle"
              size="md"
            />
          </template>
          <div
            class=""
            v-html="i18n.error_reporting_csv_note_details"
          />
        </q-banner>
      </div>
      <q-separator />
      <div class="row q-mt-lg q-px-md justify-between">
        <a
          target="_blank"
          :href="exchange_account?.importer_errors_download_link"
          download
        >
          <q-btn
            unelevated
            color="primary"
            :label="i18n.error_reporting_download_action"
            outline
            icon="las la-file-download"
            no-caps
          />
        </a>

        <!-- Delete ExchangeAccount -->
        <q-btn
          unelevated
          color="negative"
          class="btn-underline"
          flat
          :label="i18n.error_reporting_account_delete"
          no-caps
          @click="exchange_account_delete_confirm"
        />
      </div>
      <q-card-actions align="right">
        <q-btn
          v-close-popup
          flat
          :label="$q.lang.label.close"
          color="primary"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Import Files Dialog -->
  <q-dialog
    v-model="exchange_account_uploads_dialog_show"
    persistent
  >
    <q-card
      v-if="!exchange_account_importer_selected"
      style="width: 700px; max-width: 80vw"
    >
      <q-card-section class="row items-center">
        <div class="text-h6">
          {{ i18n.exchange_account_uploads_dialog_title }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <q-list>
          <q-item
            v-for="importer in exchange_account_importers"
            :key="importer.name"
            clickable
            @click="exchange_account_uploads_importer_select(importer)"
          >
            <q-item-section avatar>
              <q-avatar
                v-if="importer.id === 'comptacrypto-csv'"
                class="bg-black"
              >
                <q-img
                  src="/cc_icons/cc_white.svg"
                  width="24px"
                  fit="contain"
                />
              </q-avatar>
              <q-avatar
                v-else
                :style="`background-color: ${exchange_account.exchange_color}`"
              >
                <img :src="exchange_account.logo_url">
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-capitalize">
                {{ importer.label }}
              </q-item-label>
              <q-item-label caption>
                {{ importer.description }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <div class="text-center q-mt-xl">
          <!-- documentation-chip -->
          <div class="col-12">
            <span class="text-caption">{{ i18n.exchange_account_uploads_dialog_doc_label }}</span>
            <q-chip
              clickable
              color="pastel-green"
              size="12px"
              class="q-ml-sm"
              text-color="black"
              icon="menu_book"
              :label="i18n.exchange_account_uploads_dialog_doc_title"
              @click="$intercom.article_open('11836927')"
            />
          </div>
        </div>
      </q-card-section>
    </q-card>

    <q-card
      v-else
      style="width: 700px; max-width: 80vw"
    >
      <q-card-section class="row items-center">
        <div class="col text-left">
          <q-btn
            class="inline"
            flat
            icon="las la-arrow-left"
            color="primary"
            @click="exchange_account_importer_selected = null"
          />
        </div>
        <div class="col-10 text-center text-h6">
          {{ i18n.exchange_account_uploads_dialog_exchange_title }} {{ exchange_account_importer_selected.label }}
        </div>
        <div class="col text-right">
          <q-btn
            v-close-popup
            class="inline"
            icon="close"
            flat
            round
            dense
          />
        </div>
      </q-card-section>
      <q-list>
        <q-item>
          <q-item-section avatar>
            <q-avatar
              v-if="exchange_account_importer_selected.id === 'comptacrypto-csv'"
              class="bg-black"
            >
              <q-img
                src="/cc_icons/cc_white.svg"
                width="24px"
                fit="contain"
              />
            </q-avatar>
            <q-avatar
              v-else
              :style="`background-color: ${exchange_account.exchange_color}`"
            >
              <img :src="exchange_account.logo_url">
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-capitalize">
              {{ exchange_account_importer_selected.label }}
            </q-item-label>
            <q-item-label caption>
              {{ exchange_account_importer_selected.description }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
      <q-card-section>
        <Uploader
          :multiple="true"
          @signed_id="exchange_account_import_sources_add"
        />
      </q-card-section>
    </q-card>
  </q-dialog>

  <!-- Initial Balance Dialog -->
  <InitialBalanceDialog
    v-if="exchange_account"
    v-model="initial_balance_dialog_show"
    :exchange-account="exchange_account"
    :workspace-id="$page.props.shared.workspace.id"
    :fiat-symbol="$page.props.shared.workspace.fiat_symbol"
    :i18n="i18n"
    @submitted="handleInitialBalanceCreated"
  />
</template>
<script>
import Layout from '@/layouts/workspace.vue';
import ClipboardCopy from '@/shared/clipboard_copy.vue';
import Uploader from '@/shared/uploader.vue';
import InitialBalanceDialog from '@/components/InitialBalanceDialog.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    ClipboardCopy,
    InitialBalanceDialog,
    Link,
    Uploader,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    exchanges_in_maintenance: {
      type: Array,
      required: true,
    },
    exchange_accounts: {
      type: Object,
      required: true,
    },
    exchange_account: {
      type: Object,
      required: false,
    },
    exchange_accounts_search: {
      type: String,
      required: false,
    },
    exchange_account_import_sources_add_action: {
      type: String,
      required: true,
    },
    exchange_account_import_source_rm_action: {
      type: String,
      required: true,
    },
    links: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      sync_all_loading: false,
      exchange_account_missing_change_rate: false, // TODO
      exchange_account_importers: this.exchange_account?.importers,
      exchange_account_importer_selected: null,
      exchange_account_uploads_dialog_show: false,
      exchange_account_uploads_signed_ids: [],
      view: "list",
      exchange_groups_expanded: {},
      balance_audit_show: this.exchange_account?.balance_audit_show,
      exchange_account_name: this.exchange_account?.name,
      exchange_account_update_path: this.exchange_account?.links?.update?.link,
      exchange_account_update_loading: false,
      exchange_account_rename_dialog_show: false,
      exchange_account_details_graph_selected: 'table', // 'table', 'pie'
      initial_balance_dialog_show: false,
      assets_table_pagination: {
        rowsPerPage: -1,
        sortBy: "fiat_value_formatted",
        descending: true,
      },
      assets_table_columns: [
        {
          name: "asset_symbol",
          align: "left",
          label: this.i18n.exchange_account_assets_table_column_currency,
          field: (row) => row.asset_symbol,
          sortable: true,
        },
        {
          name: "balance",
          align: "right",
          label: this.i18n.exchange_account_assets_table_column_balance,
          field: (row) => row.balance,
          sortable: true,
        },
        {
          name: "fiat_value_formatted",
          align: "right",
          field: (row) => row.fiat_value_formatted,
          label: this.i18n.exchange_account_assets_table_column_value,
          sort: (a, b, row_a, row_b) => parseFloat(row_a.fiat_value) - parseFloat(row_b.fiat_value),
          sortable: true,
        },
      ],
      assets_pie_options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "40%",
        plugins: {
          crosshair: false,
          tooltip: {
            enabled: true,
            backgroundColor: "#ffffff", // White background
            titleColor: "#000000", // Black title
            bodyColor: "#000000", // Black body
            padding: 12,
            callbacks: {
              title: function (context) {
                if (context.length > 0) {
                  const item = context[0];
                  return item.dataset.titles[item.dataIndex];
                }
                return "";
              },
              label: function (item) {
                return item.dataset.labels[item.dataIndex];
              },
            },
          },
          legend: {
            display: false,
          },
          labels: [
            {
              render: function (args) {
                return args.label;
              },
              overlap: false,
              fontStyle: 'bold',
            }
          ],
        },
      },
    };
  },
  computed: {
    exchangeAccountsArray() {
      if (Array.isArray(this.exchange_accounts)) return this.exchange_accounts;
      if (Array.isArray(this.exchange_accounts?.data)) return this.exchange_accounts.data;
      return [];
    },
    exchangeAccountGroups() {
      const groupsById = new Map();

      for (const account of this.exchangeAccountsArray) {
        const exchangeId = account.exchange_id || account.exchange_name;
        const exchangeLabel = account.exchange_name;
        const group = groupsById.get(exchangeId) || {
          exchange_id: exchangeId,
          exchange_name: exchangeLabel,
          logo_url: account.logo_url,
          accounts: [],
        };
        group.accounts.push(account);
        groupsById.set(exchangeId, group);
      }

      const groups = Array.from(groupsById.values());
      for (const group of groups) {
        group.accounts.sort((a, b) => String(a.name).localeCompare(String(b.name)));

        const status = this.pickWorstStatus(group.accounts.map((a) => a.status));
        const statusIsPrimary = this.statusRank(status) === 2;
        const anyAccountLoading = group.accounts.some((a) => a.loading);

        group.status = status;
        group.loading = statusIsPrimary && anyAccountLoading;
        group.total_transactions_count = group.accounts.reduce(
          (sum, a) => sum + Number(a.total_transactions_count || 0),
          0,
        );
        group.balance_cents = group.accounts.reduce(
          (sum, a) => sum + Number(a.balance_cents || 0),
          0,
        );
        group.assets = this.aggregateAssets(group.accounts);
        group.balance_audit_matched = group.accounts.every((a) => a.balance_audit_matched !== false);
      }

      groups.sort((a, b) => String(a.exchange_name).localeCompare(String(b.exchange_name)));
      return groups;
    },
    declaration_status() {
      const declaration = this.exchange_account?.declaration || {};
      const status = declaration.status || "unknown";
      const labels = {
        required: this.i18n.foreign_declaration.required_label,
        not_required: this.i18n.foreign_declaration.not_required_label,
        unknown: this.i18n.foreign_declaration.unknown_label || this.i18n.foreign_declaration.unknown_message,
      };
      const colors = {
        required: "negative",
        not_required: "primary",
        unknown: "grey",
      };
      const icons = {
        required: "sym_o_assignment",
        not_required: "sym_o_task_alt",
        unknown: "sym_o_help_outline",
      };
      const messages = {
        required: this.i18n.foreign_declaration.required_message,
        not_required: this.i18n.foreign_declaration.not_required_message,
        unknown: this.i18n.foreign_declaration.unknown_message,
      };
      const message_icons = {
        required: "sym_o_gavel",
        not_required: "sym_o_info",
        unknown: "sym_o_help_outline",
      };

      return {
        status,
        label: labels[status],
        color: colors[status],
        icon: icons[status],
        message: messages[status],
        message_icon: message_icons[status],
      };
    },
    declaration_has_details() {
      const decl = this.exchange_account?.declaration || {};
      return Boolean(decl.legal_entity || decl.address || decl.website || decl.source_name);
    },
  },
  watch: {
    exchange_account: {
      handler() {
        this.expand_group_for_selected_account();
      },
      immediate: true,
    },
  },
  methods: {
    accountsLabel(count) {
      const n = Number(count || 0);
      return n <= 1 ? "1 compte" : `${n} comptes`;
    },
    formatFiatFromCents(cents) {
      const currency = this.$page?.props?.shared?.workspace?.fiat_symbol || "";
      const value = Number(cents || 0) / 100;

      if (!Number.isFinite(value)) return "---";

      if (currency) {
        try {
          return new Intl.NumberFormat("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: "currency",
            currency: currency,
          }).format(value);
        } catch {
          // Fallback below
        }
      }

      return `${value.toFixed(2)} ${currency}`.trim();
    },
    is_exchange_group_expanded(exchangeId) {
      return Boolean(this.exchange_groups_expanded?.[exchangeId]);
    },
    exchange_group_toggle(exchangeId) {
      const expanded = this.is_exchange_group_expanded(exchangeId);
      this.exchange_groups_expanded = {
        ...(this.exchange_groups_expanded || {}),
        [exchangeId]: !expanded,
      };
    },
    expand_group_for_selected_account() {
      const selectedId = this.exchange_account?.id;
      if (!selectedId) return;

      const account = this.exchangeAccountsArray.find((a) => a.id === selectedId);
      const exchangeId = account?.exchange_id || account?.exchange_name;
      if (!exchangeId) return;

      if (this.is_exchange_group_expanded(exchangeId)) return;
      this.exchange_groups_expanded = {
        ...(this.exchange_groups_expanded || {}),
        [exchangeId]: true,
      };
    },
    statusRank(status) {
      const color = status?.color;
      if (color === "negative") return 0;
      if (color === "warning") return 1;
      if (color === "primary") return 2;
      if (color === "positive") return 3;
      return 1;
    },
    pickWorstStatus(statuses) {
      if (!Array.isArray(statuses) || statuses.length === 0) return {};
      return statuses.reduce((worst, current) => {
        const worstRank = this.statusRank(worst);
        const currentRank = this.statusRank(current);
        return currentRank < worstRank ? current : worst;
      }, statuses[0]);
    },
    aggregateAssets(accounts) {
      const iconsByKey = new Map();
      let extra = 0;

      for (const account of accounts || []) {
        const icons = account?.assets?.icons || [];
        for (const icon of icons) {
          const key = `${icon.symbol || ""}|${icon.url || ""}`;
          if (!iconsByKey.has(key)) iconsByKey.set(key, icon);
        }

        const assetsCount = account?.assets?.assets_count;
        if (typeof assetsCount === "string" && assetsCount.startsWith("+")) {
          const n = Number(assetsCount.slice(1));
          if (Number.isFinite(n)) extra += n;
        }
      }

      const allIcons = Array.from(iconsByKey.values());
      const topsKeepCount = 5;
      const icons = allIcons.slice(0, topsKeepCount);
      const hiddenFromTops = Math.max(0, allIcons.length - topsKeepCount);
      const totalExtra = extra + hiddenFromTops;

      return {
        icons,
        assets_count: totalExtra > 0 ? `+${totalExtra}` : null,
      };
    },
    handleInitialBalanceCreated: function () {
      // Refresh the page to show updated data
      this.$inertia.reload({
        preserveScroll: true,
        only: ['exchange_account', 'exchange_accounts']
      });
    },
    exchange_account_search_update: function (search) {
      this.$inertia.get(
        '',
        { search: search },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'exchange_accounts_search',
            'exchange_accounts',
          ],
        },
      );
    },
    exchange_account_close: function () {
      this.$inertia.get(
        '#',
        {
          exchange_account_id: null,
        },
        {
          preserveState: true,
          preserveScroll: true,
          only: ['exchange_account'],
        },
      );
    },
    exchange_account_select: function (id) {
      this.$inertia.get(
        '#',
        {
          exchange_account_id: id,
          balance_audit: null,
          importer_errors: null,
        },
        {
          preserveState: false,
          preserveScroll: true,
          only: ['exchange_account'],
        },
      );
    },
    exchange_account_open_dashboard: function () {
      if (!this.exchange_account?.id) return;

      this.$inertia.get(
        this.$page.props.shared.menu_items.dashboard.link,
        {
          exchange_account: this.exchange_account.id,
        },
        {
          preserveState: false,
          preserveScroll: true,
        },
      );
    },
    exchange_account_open_transactions: function () {
      if (!this.exchange_account?.links?.transactions?.link) return;

      this.$inertia.get(
        this.exchange_account.links.transactions.link,
        {},
        {
          preserveState: false,
          preserveScroll: true,
        },
      );
    },
    exchange_account_update: function () {
      this.$inertia.put(
        this.exchange_account_update_path,
        {
          name: this.exchange_account_name,
        },
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.exchange_account_update_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.save_success,
              type: "positive",
            });
            this.exchange_account_rename_dialog_show = false;
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.save_error,
              type: "negative",
            });
          },
          onFinish: () => {
            this.exchange_account_update_loading = false;
          }
        }
      );
    },
    exchange_account_delete_confirm: function () {
      this.$q.dialog({
        title: this.i18n.exchange_account_delete_confirm_title,
        message: this.i18n.exchange_account_delete_confirm_message,
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(() => {
        this.exchange_account_delete();
      });
    },
    exchange_account_rename_open: function () {
      this.exchange_account_name = this.exchange_account?.name;
      this.exchange_account_update_path = this.exchange_account?.links?.update?.link;
      this.exchange_account_rename_dialog_show = true;
    },
    exchange_account_delete: function () {
      this.$inertia.delete(
        this.exchange_account_update_path,
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.exchange_account_update_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.i18n.exchange_account_delete_success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.i18n.save_error,
              type: "negative",
            });
          },
          onFinish: () => {
            this.exchange_account_update_loading = false;
          }
        }
      );
    },
    exchange_account_uploads_dialog_open: function () {
      this.exchange_account_importer_selected = null;
      this.exchange_account_uploads_dialog_show = true;
    },
    exchange_account_uploads_importer_select: function (importer) {
      this.exchange_account_importer_selected = importer;
    },
    exchange_account_import_sources_add: function (blob, files_remaining) {
      // Collect the new signed_id
      this.exchange_account_uploads_signed_ids.push(blob.signed_id);

      // Auto-trigger the attachment process on the last file.
      if (files_remaining === 0) {
        if (this.exchange_account_uploads_signed_ids.length === 0) {
          return;
        }

        this.$inertia.post(
          this.exchange_account_import_sources_add_action,
          {
            exchange_account_id: this.exchange_account?.id,
            signed_ids: this.exchange_account_uploads_signed_ids,
            importer_name: this.exchange_account_importer_selected?.id,
          },
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            onStart: () => {
              this.exchange_account_uploads_loading = true;
            },
            onSuccess: () => {
              this.$q.notify({
                message: this.$page.props.flash.success,
                type: "positive",
              });
            },
            onError: (error) => {
              console.log(error);
              this.exchange_account_uploads_loading = false;
              this.$q.notify({
                message: this.$page.props.flash.alert,
                type: "negative",
              });
            },
            onFinish: () => {
              this.exchange_account_uploads_loading = false;
              this.exchange_account_uploads_signed_ids = [];
              this.exchange_account_importer_selected = null;
              this.exchange_account_uploads_dialog_show = false;
            }
          }
        );
      }
    },
    exchange_account_import_source_remove_confirm: function (is) {
      this.$q.dialog({
        title: this.i18n.exchange_account_uploads_files_delete_confirm_title,
        message: `${is.filename} \n\n ${this.i18n.exchange_account_uploads_files_delete_confirm_message}`,
        cardClass: "whitespace-pre-line",
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(() => {
        this.exchange_account_import_source_remove(is);
      });
    },
    exchange_account_import_source_remove: function (is) {
      this.$inertia.post(
        this.exchange_account_import_source_rm_action,
        {
          exchange_account_id: this.exchange_account?.id,
          import_source_id: is.id,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (error) => {
            console.log(error);
            this.exchange_account_uploads_loading = false;
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          }
        }
      );
    },
    sync_all: function () {
      this.$inertia.post(
        this.links.sync_all.link,
        {},
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
            this.exchange_account_update_loading = true;
            this.sync_all_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.sync_all_loading = false;
          }
        }
      );
    },
    setView: function () {
    },
    rule_string_length: function (str) {
      if (!str) {
        return this.i18n.required;
      } else if (str.length > 50) {
        return this.i18n.settings_account_name_max_len;
      } else {
        return true;
      }
    },
    string_ellipsis_middle(text, visible_start = 5, visible_end = 4) {
      if (text?.length == void 0) {
        return '';
      }

      if (text.length <= visible_start + visible_end) {
        return text;
      }

      const start = text.slice(0, visible_start);
      const ending = text.slice(-visible_end);
      return `${start}\u202f…\u202f${ending}`;
    },
    truncate_url_24(text) {
      if (!text) {
        return "";
      }
      return text.length <= 24 ? text : `${text.slice(0, 24)}…`;
    },
  },
};
</script>
<style>
/* Primary border color if highlited */
.highlighted {
  border-color: var(--q-accent);
}

.declaration-row {
  padding-bottom: 8px;
}
</style>
